// Elenco pratiche: colonne scegliibili e riferimento leggibile. Funzioni pure, provate in contractColumns.test.ts.

export type ContractColumnKey =
  | 'reference' | 'customer' | 'operator' | 'status' | 'collection' | 'amount'
  | 'accrued' | 'expected' | 'productType' | 'paymentMethod' | 'probability' | 'closeDate' | 'date'

export const CONTRACT_COLUMNS: { key: ContractColumnKey; label: string; defaultVisible: boolean }[] = [
  { key: 'reference', label: 'Riferimento', defaultVisible: true },
  { key: 'customer', label: 'Cliente', defaultVisible: true },
  { key: 'operator', label: 'Operatore', defaultVisible: true },
  { key: 'status', label: 'Stato', defaultVisible: true },
  { key: 'collection', label: 'Incasso', defaultVisible: true },
  { key: 'amount', label: 'Importo', defaultVisible: false },
  { key: 'expected', label: 'Previsto', defaultVisible: true },
  { key: 'accrued', label: 'Maturato', defaultVisible: true },
  { key: 'productType', label: 'Tipo Prodotto', defaultVisible: false },
  { key: 'paymentMethod', label: 'Metodo Pag.', defaultVisible: false },
  { key: 'probability', label: 'Probabilità', defaultVisible: false },
  { key: 'closeDate', label: 'Chiusura prevista', defaultVisible: false },
  { key: 'date', label: 'Data', defaultVisible: true },
]

export const CONTRACT_COLUMNS_STORAGE_KEY = 'crm.contracts.columns.v1'

export function defaultVisibleColumns(): ContractColumnKey[] {
  return CONTRACT_COLUMNS.filter(c => c.defaultVisible).map(c => c.key)
}

/** Colonne salvate, filtrate sulle chiavi note; qualsiasi problema di storage -> predefinite. */
export function loadVisibleColumns(): ContractColumnKey[] {
  try {
    const raw = window.localStorage.getItem(CONTRACT_COLUMNS_STORAGE_KEY)
    if (!raw) return defaultVisibleColumns()
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return defaultVisibleColumns()
    const known = new Set<string>(CONTRACT_COLUMNS.map(c => c.key))
    const keys = parsed.filter((k): k is ContractColumnKey => typeof k === 'string' && known.has(k))
    return keys.length > 0 ? keys : defaultVisibleColumns()
  } catch {
    return defaultVisibleColumns()
  }
}

export function saveVisibleColumns(keys: ContractColumnKey[]): void {
  try {
    window.localStorage.setItem(CONTRACT_COLUMNS_STORAGE_KEY, JSON.stringify(keys))
  } catch {
    /* storage non disponibile: la scelta vale solo per la sessione */
  }
}

/** Sposta una colonna visibile di un posto (-1 = prima, +1 = dopo); fuori dai bordi non cambia nulla. */
export function moveColumn(keys: ContractColumnKey[], key: ContractColumnKey, dir: -1 | 1): ContractColumnKey[] {
  const i = keys.indexOf(key)
  const j = i + dir
  if (i < 0 || j < 0 || j >= keys.length) return keys
  const next = [...keys]
  next[i] = keys[j]!
  next[j] = key
  return next
}

/** Codice interno EQ + ggmmaa + progressivo (EQ161026-3): si mostra com'e'. */
export function isInternalReference(code: string | null | undefined): boolean {
  return !!code && /^EQ\d{6}-\d+$/.test(code.trim())
}

/** Codice provvisorio del CRM (EQ... o il vecchio CONT-...), cioe' non ancora il codice del portale. */
export function isProvisionalReference(code: string | null | undefined): boolean {
  return isInternalReference(code) || (!!code && /^CONT-/i.test(code.trim()))
}

/** Vecchi codici tecnici (CONT-MIGR-12, CONT-IMP-7, CONT-<ms>): non dicono nulla, si sostituiscono con un riferimento leggibile. */
export function isTechnicalReference(code: string | null | undefined): boolean {
  return !code || /^CONT-/i.test(code.trim())
}

/**
 * Riferimento leggibile: il numero/codice della pratica se e' significativo, altrimenti
 * "Mandante · Operatore · data" con i pezzi disponibili; ultima spiaggia "Pratica #id".
 */
export function contractReferenceLabel(p: {
  id: number
  referenceCode?: string | null
  mandante?: string | null | undefined
  operator?: string | null | undefined
  date?: string | null
}): string {
  if (!isTechnicalReference(p.referenceCode)) return p.referenceCode!.trim()
  const parts = [p.mandante, p.operator, p.date].filter((x): x is string => !!x)
  return parts.length > 0 ? parts.join(' · ') : `Pratica #${p.id}`
}
